import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Edit, Trash2, Users, Download, Upload } from 'lucide-react';
import UserManagement from '../components/UserManagement';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { collection, getDocs, doc, setDoc, deleteDoc } from 'firebase/firestore';

const Settings = ({ user, permissions }) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('company');
  const [loading, setLoading] = useState(false);
  const [companyDetails, setCompanyDetails] = useState({
    name: 'ADDICT',
    address: '123 Business Street',
    city: 'Colombo, Sri Lanka',
    phone: '+94 11 234 5678',
    email: 'info@addict.lk'
  });


  useEffect(() => {
    const storedCompany = localStorage.getItem('addictCompany');
    if (storedCompany) {
      setCompanyDetails(JSON.parse(storedCompany));
    }
  }, []);

  const saveCompany = (data) => localStorage.setItem('addictCompany', JSON.stringify(data));

  const handleBackup = async () => {
    try {
      setLoading(true);
      const collections = ['inventory', 'orders', 'customers', 'sales', 'users'];
      const backup = { timestamp: new Date().toISOString(), data: {} };
      
      for (const collectionName of collections) {
        const snapshot = await getDocs(collection(db, collectionName));
        backup.data[collectionName] = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      }
      
      const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `addict-backup-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      
      alert('Backup created successfully!');
    } catch (error) {
      console.error('Backup failed:', error);
      alert('Backup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    try {
      setLoading(true);
      const text = await file.text();
      const backup = JSON.parse(text);
      
      if (!backup.data) {
        alert('Invalid backup file format.');
        return;
      }
      
      if (!window.confirm('This will replace all existing data. Are you sure?')) {
        return;
      }
      
      for (const [collectionName, documents] of Object.entries(backup.data)) {
        // Clear existing data
        const snapshot = await getDocs(collection(db, collectionName));
        for (const docSnapshot of snapshot.docs) {
          await deleteDoc(doc(db, collectionName, docSnapshot.id));
        }
        
        // Import new data
        for (const document of documents) {
          const { id, ...data } = document;
          await setDoc(doc(db, collectionName, id), data);
        }
      }
      
      alert('Data imported successfully! Please refresh the page.');
    } catch (error) {
      console.error('Import failed:', error);
      alert('Import failed. Please check the file format.');
    } finally {
      setLoading(false);
      event.target.value = '';
    }
  };

  const handleSaveCompany = () => {
    saveCompany(companyDetails);
    alert('Company details saved successfully!');
  };



  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>Loading...</div>;
  }

  return (
    <div className="inventory-page">
      <div className="inventory-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button 
            className="btn btn-secondary"
            onClick={() => navigate('/dashboard')}
          >
            <ArrowLeft size={16} style={{ marginRight: '8px' }} />
            Back
          </button>
          <h1 className="inventory-title">Settings</h1>
        </div>
        
        <div className="inventory-actions">
          <div style={{ display: 'flex', gap: '10px' }}>
            <button 
              className={`btn ${activeTab === 'company' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setActiveTab('company')}
            >
              Company
            </button>
            {(currentUser?.role === 'admin' || permissions?.settings) && (
              <>
                <button 
                  className={`btn ${activeTab === 'users' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setActiveTab('users')}
                >
                  <Users size={16} style={{ marginRight: '8px' }} />
                  Users
                </button>
                <button 
                  className={`btn ${activeTab === 'backup' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setActiveTab('backup')}
                >
                  <Download size={16} style={{ marginRight: '8px' }} />
                  Backup
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="inventory-content">
        {activeTab === 'company' && (
          <div style={{
            background: 'white',
            borderRadius: '20px',
            padding: '30px',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)'
          }}>
            <h2 style={{ color: '#333', margin: '0 0 20px 0' }}>Company Details</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
              <div className="form-group">
                <label>Company Name</label>
                <input
                  type="text"
                  value={companyDetails.name}
                  onChange={(e) => setCompanyDetails({...companyDetails, name: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Address</label>
                <input
                  type="text"
                  value={companyDetails.address}
                  onChange={(e) => setCompanyDetails({...companyDetails, address: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>City</label>
                <input
                  type="text"
                  value={companyDetails.city}
                  onChange={(e) => setCompanyDetails({...companyDetails, city: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="text"
                  value={companyDetails.phone}
                  onChange={(e) => setCompanyDetails({...companyDetails, phone: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={companyDetails.email}
                  onChange={(e) => setCompanyDetails({...companyDetails, email: e.target.value})}
                />
              </div>
            </div>
            <button onClick={handleSaveCompany} className="btn btn-primary" style={{ marginTop: '20px' }}>
              Save Company Details
            </button>
          </div>
        )}
        
        {activeTab === 'backup' && (
          <div style={{
            background: 'white',
            borderRadius: '20px',
            padding: '30px',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)'
          }}>
            <h2 style={{ color: '#333', margin: '0 0 20px 0' }}>Backup & Import</h2>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '30px' }}>
              <div style={{ padding: '20px', border: '2px solid #e9ecef', borderRadius: '12px' }}>
                <h3 style={{ color: '#667eea', margin: '0 0 15px 0' }}>Create Backup</h3>
                <p style={{ color: '#666', marginBottom: '20px' }}>Download a complete backup of all your data including inventory, orders, customers, and sales.</p>
                <button 
                  onClick={handleBackup} 
                  className="btn btn-primary"
                  disabled={loading}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <Download size={16} />
                  {loading ? 'Creating Backup...' : 'Download Backup'}
                </button>
              </div>
              
              <div style={{ padding: '20px', border: '2px solid #e9ecef', borderRadius: '12px' }}>
                <h3 style={{ color: '#e74c3c', margin: '0 0 15px 0' }}>Import Data</h3>
                <p style={{ color: '#666', marginBottom: '20px' }}>Import data from a backup file. This will replace all existing data.</p>
                <input 
                  type="file" 
                  accept=".json" 
                  onChange={handleImport}
                  disabled={loading}
                  style={{ display: 'none' }}
                  id="import-file"
                />
                <label 
                  htmlFor="import-file" 
                  className="btn btn-secondary"
                  style={{ 
                    display: 'inline-flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.6 : 1
                  }}
                >
                  <Upload size={16} />
                  {loading ? 'Importing...' : 'Select Backup File'}
                </label>
              </div>
            </div>
            
            <div style={{ marginTop: '30px', padding: '20px', background: '#fff3cd', borderRadius: '8px', border: '1px solid #ffeaa7' }}>
              <h4 style={{ color: '#856404', margin: '0 0 10px 0' }}>Important Notes:</h4>
              <ul style={{ color: '#856404', margin: 0, paddingLeft: '20px' }}>
                <li>Backups include all data: inventory, orders, customers, sales, and users</li>
                <li>Import will completely replace existing data - create a backup first</li>
                <li>Only import backup files created by this system</li>
                <li>Refresh the page after importing data</li>
              </ul>
            </div>
          </div>
        )}
        
        {activeTab === 'users' && (currentUser?.role === 'admin' || permissions?.settings) && (
          <UserManagement />
        )}
      </div>
    </div>
  );
};

export default Settings;